<template>
  <div
    v-if="isVisibleHeaderPill"
    class="header-discount-pill"
    data-testid="headerDiscountPill"
  >
    <CreditLimitPill
      v-if="showCreditPillSlot"
      :is-visible="isDesktopHeaderViewport"
    />
    <Pill
      v-if="showPromoPill && !isEnclosedPage"
      :content="headerPill.pill"
      data-testid="pill"
    />
    <ProgramPill
      v-if="showProgramPill && !isEnclosedPage"
      :program-name-route="routeAnkorstorePlus"
      data-testid="programPill"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMediaQuery } from '@vueuse/core';

import CreditLimitPill from '@monolith/legacy/components/header/credit-limit-pill.vue';
import { useCreditLimit } from '@modules/account/retailer-settings/composables/use-credit-limit';
import Pill from '@monolith/legacy/modules/promote-engage/pills/pill.vue';
import { getCmsHeaderPills, CmsHeaderPillsAttributes } from '@monolith/legacy/modules/promote-engage/services/pill';
import ProgramPill from '@monolith/legacy/modules/promote-engage/subscription-program/program-pill.vue';

const MAX_PILLS = 2;

export default defineComponent({
  name: 'HeaderPill',
  components: {
    ProgramPill,
    Pill,
    CreditLimitPill,
  },
  props: {
    isEnclosedPage: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const store = useStore();
    const { showCreditLimitPill, isBusinessVerificationPending, setCreditLimitFromCms } = useCreditLimit();

    // The verification pill (RCN-498) occupies the same header slot as the credit limit pill and
    // takes precedence over it — see use-credit-limit-pill.ts for why they're mutually exclusive.
    const showCreditPillSlot = computed(() => showCreditLimitPill.value || isBusinessVerificationPending.value);

    // Mirrors #header's `media-breakpoint-up(xl)` CSS rule (xl = 1280px) — this container is
    // only visually shown at/above that width; #headerMobile covers the rest. Passed down so
    // CreditLimitPill's impression tracking doesn't double-count across both CSS-toggled mounts.
    const isDesktopHeaderViewport = useMediaQuery('(min-width: 1280px)');

    const userIsRetailer = computed(() => store.getters['userIsRetailer']);

    const headerPill = ref<CmsHeaderPillsAttributes | null>(null);

    // Priority order: CreditLimit > Pill > ProgramPill (max 2 pills)
    const showPromoPill = computed(() => !!headerPill.value?.pill);
    const hasAnkorstorePlus = computed(() => headerPill.value && 'ankorstore_plus_opt_in' in headerPill.value);

    const showProgramPill = computed(() => {
      const higherPriorityCount = (showCreditPillSlot.value ? 1 : 0) + (showPromoPill.value ? 1 : 0);
      return hasAnkorstorePlus.value && higherPriorityCount < MAX_PILLS;
    });

    const routeAnkorstorePlus = computed(() => {
      if (!headerPill.value) {
        return '';
      }
      return headerPill.value.ankorstore_plus_opt_in ? 'ankorstore-plus' : 'ankorstore-plus-signup';
    });

    const isVisibleHeaderPill = computed((): boolean => {
      // On enclosed pages (checkout, cart, ...), only show if credit limit pill is visible
      if (props.isEnclosedPage) {
        return showCreditPillSlot.value;
      }
      return !!headerPill.value || showCreditPillSlot.value;
    });

    onMounted(async () => {
      if (!userIsRetailer.value) {
        return;
      }
      const cmsData = await getCmsHeaderPills();
      headerPill.value = cmsData;
      setCreditLimitFromCms(cmsData?.credit_limit, cmsData?.business_verification);
    });

    return {
      headerPill,
      isDesktopHeaderViewport,
      isVisibleHeaderPill,
      routeAnkorstorePlus,
      showCreditPillSlot,
      showPromoPill,
      showProgramPill,
    };
  },
});
</script>
<style lang="scss" scoped>
.header-discount-pill {
  @apply ds-flex ds-gap-6 ds-items-center;
}
</style>
